import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { User, Settings, BookOpen, TrendingUp } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import GuidelinesModal from '../../../components/GuidelinesModal';

export default function TwinOverview() {
  const { t } = useTranslation();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [showGuidelines, setShowGuidelines] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchProgress();
    
    // Show guidelines if redirected from onboarding
    const params = new URLSearchParams(location.search);
    if (params.get('onboarding') === 'complete') {
      setShowGuidelines(true);
    }
  }, [location]);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get('/personal/profile');
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await apiClient.get('/personal/training-progress');
      setTrainingProgress(response.data.overall);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const isSubPage = location.pathname !== '/personal/twin';

  if (isSubPage) {
    return <Outlet />;
  }
  
  // Show guidelines modal
  if (showGuidelines) {
    return (
      <>
        <GuidelinesModal open={showGuidelines} onClose={() => setShowGuidelines(false)} />
        {renderContent()}
      </>
    );
  }
  
  return renderContent();
  
  function renderContent() {
    if (loading) {
      return (
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      );
    }
    
    return (
      <>
      {renderMainContent()}
      </>
    );
  }
  
  function renderMainContent() {

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="twin-overview">
      <div>
        <h1 className="font-heading text-4xl font-bold text-gray-900">{t('twin.title')}</h1>
        <p className="text-lg text-gray-600 mt-2">{t('twin.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('twin.identity')}</h3>
              <p className="text-sm text-gray-500">{t('twin.profilePersona')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-2">{profile?.twinName || t('common.notSet')}</p>
          <p className="text-sm text-gray-600 mb-4 capitalize">
            {profile?.responseTone || 'casual'} {t('twinOverview.tone')}
          </p>
          <Link to="/personal/twin/profile">
            <Button variant="outline" size="sm" className="w-full">
              {t('twin.editProfile')}
            </Button>
          </Link>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-personal-sage/10 rounded-xl">
              <TrendingUp className="w-6 h-6 text-personal-sage" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('twin.trainingProgress')}</h3>
              <p className="text-sm text-gray-500">{t('twin.trainingModes')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-2">{trainingProgress}%</p>
          <p className="text-sm text-gray-600 mb-4">
            {profile?.trainingModes?.length || 0} {t('twin.modesActive')}
          </p>
          <Link to="/personal/twin/train">
            <Button variant="outline" size="sm" className="w-full">
              {t('training.trainStyle')}
            </Button>
          </Link>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-personal-warm/10 rounded-xl">
              <Settings className="w-6 h-6 text-personal-warm" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('nav.settings')}</h3>
              <p className="text-sm text-gray-500">{t('twin.behaviorBoundaries')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-2">{t('twin.configured')}</p>
          <p className="text-sm text-gray-600 mb-4">{t('twin.responseSettings')}</p>
          <Link to="/personal/twin/preferences">
            <Button variant="outline" size="sm" className="w-full">
              {t('twin.editSettings')}
            </Button>
          </Link>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('twinOverview.twinSkills')}</h3>
              <p className="text-sm text-gray-500">{t('twinOverview.installedCapabilities')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-2">{t('twinOverview.skills')}</p>
          <p className="text-sm text-gray-600 mb-4">
            {t('twinOverview.manageInstalledSkills')}
          </p>
          <Link to="/personal/twin/skills">
            <Button variant="outline" size="sm" className="w-full">
              {t('twinOverview.manageSkills')}
            </Button>
          </Link>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Settings className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('twinOverview.customSkills')}</h3>
              <p className="text-sm text-gray-500">{t('twinOverview.yourPrivateSkills')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-2">{t('twinOverview.custom')}</p>
          <p className="text-sm text-gray-600 mb-4">
            {t('twinOverview.createManageCustomSkills')}
          </p>
          <Link to="/personal/twin/custom-skills">
            <Button variant="outline" size="sm" className="w-full">
              {t('twinOverview.viewCustomSkills')}
            </Button>
          </Link>
        </Card>
      </div>

      <Card className="p-6 rounded-2xl">
        <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">
          {t('twinOverview.personalityProfile')}
        </h3>
        {profile?.personality && Object.keys(profile.personality).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(profile.personality).map(([trait, value]) => (
              <div key={trait} className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium capitalize">{trait}</span>
                  <span className="text-sm text-gray-500">{value}/10</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${(value / 10) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">{t('twinOverview.noPersonalityTraits')}</p>
        )}
      </Card>

      <Card className="p-6 rounded-2xl">
        <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">
          {t('twinOverview.quickActions')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/personal/twin/profile">
            <Button variant="outline" className="w-full justify-start">
              <User className="w-4 h-4 mr-3" />
              {t('twinOverview.updateProfile')}
            </Button>
          </Link>
          <Link to="/personal/twin/train">
            <Button variant="outline" className="w-full justify-start">
              <BookOpen className="w-4 h-4 mr-3" />
              {t('twinOverview.addTrainingData')}
            </Button>
          </Link>
          <Link to="/personal/twin/preferences">
            <Button variant="outline" className="w-full justify-start">
              <Settings className="w-4 h-4 mr-3" />
              {t('twinOverview.adjustSettings')}
            </Button>
          </Link>
        </div>
      </Card>
    </div>
    );
  }
}
